import type { SchemaDriftReport } from '../services/types';

export interface SchemaDriftBannerProps {
  drift: SchemaDriftReport | null | undefined;
}

/**
 * Visible warning on the Schema tab when the Recursive Schema Validator
 * (``core.schema_guard``) detects drift between this run's schema and the
 * previous baseline for the same dataset.
 *
 * Rendering rules
 * ---------------
 * - ``drift`` is null / undefined / drift_class === "none" → nothing
 *   rendered (no baseline to compare, or no drift detected).
 * - drift_class === "minor" → yellow info banner. Reviewer should know
 *   but the pipeline is still safe; additive / widening changes.
 * - drift_class === "major" → red warning banner with per-column
 *   detail. A downstream consumer may reject the new schema.
 *
 * v0.1 note
 * ---------
 * The backend has no persistent baseline across runs, so in v0.1 this
 * component effectively never renders. It's shipped as ready
 * infrastructure: the moment v0.2 retains prior schemas and populates
 * ``RunSummary.schema_drift``, the banner lights up without a
 * component change.
 */
export function SchemaDriftBanner({ drift }: SchemaDriftBannerProps) {
  if (!drift || drift.drift_class === 'none') return null;

  const isMajor = drift.drift_class === 'major';
  const tone = isMajor
    ? 'border-danger/40 bg-danger/10 text-danger'
    : 'border-warn/40 bg-warn/10 text-warn';

  return (
    <aside
      role={isMajor ? 'alert' : 'status'}
      aria-live={isMajor ? 'assertive' : 'polite'}
      className={`rounded-md border p-4 space-y-2 text-sm ${tone}`}
    >
      <p className="font-semibold uppercase tracking-wide text-xs">
        Schema drift detected ({drift.drift_class})
      </p>
      <p>
        {isMajor
          ? 'This run produced a schema that differs from the previous baseline in breaking ways. Downstream consumers may reject the output.'
          : 'This run produced a schema that differs from the previous baseline. Changes are additive or safe; review before relying on downstream consistency.'}
        {drift.baseline_version && drift.current_version && (
          <>
            {' '}
            <span className="text-xs font-mono opacity-75">
              baseline {drift.baseline_version.slice(0, 16)} → current{' '}
              {drift.current_version.slice(0, 16)}
            </span>
          </>
        )}
      </p>
      {drift.entries.length > 0 && (
        <ul className="mt-2 space-y-1 text-xs font-mono">
          {drift.entries.map((e, i) => (
            <li key={`${e.column}-${e.kind}-${i}`} className="flex gap-2">
              <span className="font-semibold">{e.column}</span>
              <span className="opacity-75">{e.kind}</span>
              {e.baseline && e.current ? (
                <span>
                  {e.baseline} → {e.current}
                </span>
              ) : null}
              {e.note ? <span className="italic opacity-75">({e.note})</span> : null}
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
}
